export const errorMessages: Record<string, string> = {
  // Erros de validação
  'validation_failed': 'Verifique os dados informados e tente novamente.',
  
  // Erros de duplicidade
  'email_already_exists': 'Este e-mail pessoal já está cadastrado em nossa base.',
  'associate_email_already_exists': 'Este e-mail profissional já está em uso.',
  'cpf_already_exists': 'Este CPF já está cadastrado.',
  'mobile_already_exists': 'Este número de celular já está em uso.',
  'duplicate_entry': 'Já existe um registro com estas informações.',
  
  // Erros de autenticação/autorização
  'unauthorized': 'Você não tem permissão para realizar esta ação.',
  'invalid_credentials': 'E-mail ou senha incorretos.',
  
  // Erros de servidor
  'internal_server_error': 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
  'service_unavailable': 'Serviço temporariamente indisponível.',
  
  // Erros de rede
  'network_error': 'Erro de conexão. Verifique sua internet e tente novamente.',
  
  // Mensagem padrão para erros não mapeados
  'default': 'Ocorreu um erro inesperado. Tente novamente.'
};

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

export interface ApiErrorResponse {
  success: false;
  error: ApiError;
}

export const getApiErrorMessage = (
  data: unknown,
  fallback: string,
): string => {
  if (!data || typeof data !== 'object') return fallback;

  const response = data as {
    message?: unknown;
    error?: { message?: unknown } | string;
  };
  const rawMessage =
    response.message ??
    (typeof response.error === 'object'
      ? response.error?.message
      : response.error);

  if (typeof rawMessage === 'string' && rawMessage.trim()) {
    return rawMessage;
  }

  if (Array.isArray(rawMessage)) {
    const messages = rawMessage
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'message' in item) {
          const message = (item as { message?: unknown }).message;
          return typeof message === 'string' ? message : null;
        }
        return null;
      })
      .filter((message): message is string => Boolean(message));

    if (messages.length > 0) return [...new Set(messages)].join(' ');
  }

  return fallback;
};

// Função para identificar se um erro é da nossa API padronizada
export const isApiErrorResponse = (error: any): error is ApiErrorResponse => {
  return error && error.error && error.error.code;
};

// Função principal para tratar erros da API
export const handleApiError = (error: any): ApiError => {
  // Se for um erro de rede ou não tiver resposta
  if (!error.response) {
    return {
      code: 'network_error',
      message: errorMessages.network_error
    };
  }
  
  const { data, status } = error.response;
  
  if (isApiErrorResponse(data)) {
    const { code, details, message } = data.error;
    if (code === 'validation_failed') {
      return {
        code,
        message: 'Verifique os campos destacados',
        details
      };
    }
    
    return {
      code,
      message: errorMessages[code] || message || errorMessages.default,
      details
    };
  }
  
  // Para erros não padronizados, tentamos inferir com base no status HTTP
  let code = 'unknown_error';
  let message = errorMessages.default;
  
  switch (status) {
    case 400:
      code = 'bad_request';
      message = 'Requisição inválida. Verifique os dados enviados.';
      break;
    case 401:
      code = 'unauthorized';
      message = 'Sua sessão expirou. Faça login novamente.';
      break;
    case 403:
      code = 'forbidden';
      message = 'Você não tem permissão para acessar este recurso.';
      break;
    case 404:
      code = 'not_found';
      message = 'Recurso não encontrado.';
      break;
    case 500:
      code = 'internal_server_error';
      message = 'Erro interno do servidor. Tente novamente mais tarde.';
      break;
    case 503:
      code = 'service_unavailable';
      message = 'Serviço temporariamente indisponível. Tente novamente mais tarde.';
      break;
  }
  
  return { code, message };
};

// Função para extrair mensagens de erro de campos específicos
export const getFieldError = (error: ApiError, fieldName: string): string | null => {
  if (!error.details) return null;

  if (error.details.fieldErrors?.[fieldName]?.length) {
    return error.details.fieldErrors[fieldName][0];
  }

  // if (Array.isArray(error.details)) {
  //   const fieldError = error.details.find((detail: any) => detail.path === fieldName);
  //   return fieldError ? fieldError.message : null;
  // }
  
  return null;
};

export const showErrorToast = (error: ApiError, toaster: any) => {
  toaster.create({
    title: 'Erro',
    description: error.message,
    type: 'error',
    duration: 5000,
  });
};
